'use client';

import {
    Suspense,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useInsertionEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getRumDocumentPage, takeRumNavigationStart } from '@/features/observability/page/navigation';
import { useSyncExternalStore } from 'react';
import {
    captureRumGeneration,
    captureRumIdentityRevision,
    isRumGenerationCurrent,
    reportRumAction,
    reportRumError,
    reportRumDuration,
    setRumViewName,
    subscribeRumGenerationInvalidation,
} from '@/lib/datadog/rum';
import { resolveRumViewName } from '@/features/observability/page/view-name';
import {
    isActionableUnhandledError,
    isPageApi5xx,
    isRumMainContentBlank,
    RumPageLifecycle,
    type RumPageOutcome,
} from '@/features/observability/page/lifecycle';
import { getCorePageReady } from '@/features/observability/page/core-ready';
import { normalizeOfficePathname } from '@/features/observability/page/view-name';
import { getConsentSnapshot, getServerConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { FINAL_HTTP_ERROR_EVENT, subscribeApiHttpEvent } from '@/lib/http-events';

interface PageRuntimeContext {
    lifecycle: RumPageLifecycle;
    key: string;
    page: string;
    setOnboarding: (open: boolean) => void;
    registerProjectObserver: (stop: () => void) => void;
}
const PageContext = createContext<PageRuntimeContext | null>(null);
const noOutcome = () => null;
const noSubscription = () => () => undefined;
let activeRuntime: { lifecycle: RumPageLifecycle; pathname: string } | null = null;

/** 同步通知原生事实，让消费方在 React 合并更新前保留自己的首个合格终点。 */
export function useRumPageOutcome(onOutcome?: (outcome: RumPageOutcome | null) => void): RumPageOutcome | null {
    const runtime = useContext(PageContext);
    const observer = useRef(onOutcome);
    useInsertionEffect(() => { observer.current = onOutcome; }, [onOutcome]);
    const lifecycle = runtime?.lifecycle;
    const subscribe = useCallback((listener: () => void) => {
        if (!lifecycle) return noSubscription();
        return lifecycle.subscribeOutcome(() => {
            observer.current?.(lifecycle.getOutcome());
            listener();
        });
    }, [lifecycle]);
    return useSyncExternalStore(subscribe, lifecycle?.getOutcome ?? noOutcome, noOutcome);
}

/** HTTP 层只有通用错误通知，页面范围的判定留在 Enter 宿主。 */
export function reportRumPageHttpError(error: unknown): void {
    if (!activeRuntime) return;
    const { lifecycle, pathname } = activeRuntime;
    if (lifecycle.current && isPageApi5xx(error, lifecycle.current.page, pathname)) {
        lifecycle.abnormal('api_5xx');
    }
}

/** 必须由真实数据/合法空态或输入区已挂载的信号调用；passive 阶段晚于宿主视图 layout。 */
export function useRumPageStatus(page: string, ready: boolean, failed = false): void {
    const runtime = useContext(PageContext);
    useEffect(() => {
        if (!runtime || runtime.page !== page || runtime.lifecycle.current?.key !== runtime.key)
            return;
        if (failed) {
            getCorePageReady()?.cancel();
            runtime.lifecycle.observeOutcome('error');
        } else if (ready) {
            if (document.visibilityState === 'visible') getCorePageReady()?.ready(page);
            runtime.lifecycle.observeOutcome(document.visibilityState === 'hidden' ? 'hidden' : 'ready');
        }
    }, [runtime, page, ready, failed]);
}

/** 数据完成后仍等待真实输入框，避免把项目骨架或懒加载面板算作 ready。 */
export function useRumProjectPageStatus(dataReady: boolean, failed: boolean): void {
    const runtime = useContext(PageContext);
    useRumPageStatus('/project/?', false, failed);
    useEffect(() => {
        if (!runtime || runtime.page !== '/project/?' || !dataReady || failed) return;
        let settled = false;
        const check = () => {
            if (settled || runtime.lifecycle.current?.key !== runtime.key) return;
            const input = document.querySelector<HTMLElement>(
                '.main-chat-panel .chatbox-root [contenteditable="true"], .main-chat-panel .chatbox-root textarea:not(:disabled)'
            );
            if (!input || input.closest('[aria-disabled="true"], [inert], [aria-hidden="true"]'))
                return;
            const bounds = input.getBoundingClientRect();
            if (bounds.width <= 0 || bounds.height <= 0) return;
            settled = true;
            if (document.visibilityState === 'visible') getCorePageReady()?.ready('/project/?');
            runtime.lifecycle.observeOutcome(document.visibilityState === 'hidden' ? 'hidden' : 'ready');
            observer?.disconnect();
        };
        const observer = new MutationObserver(check);
        observer.observe(document.querySelector('.App') ?? document.body, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['contenteditable', 'aria-disabled', 'disabled', 'class', 'style'],
        });
        const frame = requestAnimationFrame(check);
        const stop = () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
        runtime.registerProjectObserver(stop);
        check();
        return stop;
    }, [dataReady, failed, runtime]);
}

/** 引导实际打开后才覆盖底层页面；关闭时底层页面开始一次新的访问。 */
export function useRumOnboardingPage(open: boolean, ready: boolean): void {
    const runtime = useContext(PageContext);
    const setOnboarding = runtime?.setOnboarding;
    useLayoutEffect(() => {
        if (!open || !setOnboarding) return;
        getCorePageReady()?.cancel();
        setOnboarding(true);
        return () => setOnboarding(false);
    }, [open, setOnboarding]);
    useRumPageStatus('/workspace/?/onboarding', open && ready);
}

export function useRumPageBoundary(): (error: Error, region: string, blocking?: boolean) => void {
    const runtime = useContext(PageContext);
    return useCallback(
        (error, region, blocking = true) => {
            if (runtime?.lifecycle.current?.key === runtime?.key) {
                runtime?.lifecycle.boundary(error, 'region', region, blocking);
            }
        },
        [runtime]
    );
}

type PageLocation = { pathname: string; search: string };

function RumPageNavigation({ onChange }: {
    /** 将框架路由事实交给观测，不包裹或等待业务子树。 */
    onChange: (location: PageLocation) => void;
}) {
    const pathname = usePathname() ?? '/';
    const search = useSearchParams()?.toString() ?? '';
    useLayoutEffect(() => onChange({ pathname, search }), [onChange, pathname, search]);
    return null;
}

/** 位于业务 Gate 外，消费 Next 路由和真实 Surface 的就绪信号，不阻塞页面。 */
export function RumPageRuntime({ children }: { /** 应用页面与入口 Gate。 */ children: ReactNode }) {
    const [location, setLocation] = useState<PageLocation | null>(null);
    const consent = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot);
    const [onboardingPath, setOnboardingPath] = useState<string | null>(null);
    const stopWaitingRef = useRef<() => void>(() => undefined);
    const stopProjectObserver = useRef<() => void>(() => undefined);
    const registerProjectObserver = useCallback((stop: () => void) => {
        stopProjectObserver.current = stop;
    }, []);
    const [lifecycle] = useState(() => new RumPageLifecycle({
            initialPage: typeof window === 'undefined' ? '/' : getRumDocumentPage(),
            navigationStart: takeRumNavigationStart,
            now: () => performance.now(),
            captureGeneration: captureRumGeneration,
            isGenerationCurrent: isRumGenerationCurrent,
            reportAction: reportRumAction,
            reportError: reportRumError,
            startView: setRumViewName,
            createId: () => crypto.randomUUID(),
        }));
    useLayoutEffect(() => {
        lifecycle.setOnSettled(() => {
            stopWaitingRef.current();
            stopProjectObserver.current();
        });
        return () => lifecycle.setOnSettled(undefined);
    }, [lifecycle]);
    const pathname = normalizeOfficePathname(location?.pathname ?? '/');
    const setOnboarding = useCallback(
        (open: boolean) => setOnboardingPath(open ? pathname : null),
        [pathname]
    );
    const page = resolveRumViewName(pathname, location?.search ?? '', onboardingPath === pathname);
    const key = `${pathname}:${page}`;
    const value = useMemo(
        () => ({ lifecycle, key, page, setOnboarding, registerProjectObserver }),
        [lifecycle, key, page, setOnboarding, registerProjectObserver]
    );

    useLayoutEffect(() => {
        if (!location) return;
        const pending = consent.cookieAvailable && (consent.resolution === 'pending'
            || (consent.resolution === 'resolved' && consent.data?.hasChoice === false));
        lifecycle.setConsent(consent.analyticsAllowed ? 'granted' : pending ? 'pending' : 'denied');
        lifecycle.open(key, page, document.visibilityState === 'hidden');
        const coreReady = getCorePageReady();
        coreReady?.visit(pathname, page);
        coreReady?.configure(
            {
                enabled: consent.analyticsAllowed,
                generation: captureRumIdentityRevision(),
                isGenerationCurrent: isRumGenerationCurrent,
                report: reportRumDuration,
            },
            !consent.analyticsAllowed && !pending
        );
        activeRuntime = { lifecycle, pathname };
        lifecycle.setArmed(true);
    }, [consent, key, lifecycle, location, page, pathname]);

    useEffect(() => {
        const visit = lifecycle.current;
        if (!visit || visit.blocked || visit.outcome) return;
        const generation = visit.generation;
        const unsubscribe =
            generation === null
                ? undefined
                : subscribeRumGenerationInvalidation(generation, () => {
                      getCorePageReady()?.cancel();
                      lifecycle.invalidate();
                  });
        let sampleTimer: ReturnType<typeof setTimeout> | undefined;
        const sample = () => {
            if (
                lifecycle.current !== visit ||
                visit.outcome ||
                visit.blocked ||
                document.visibilityState === 'hidden'
            )
                return;
            const roots = document.querySelectorAll<HTMLElement>('[data-rum-page-main]');
            const root = roots.length ? roots[roots.length - 1]! : null;
            if (isRumMainContentBlank(root)) lifecycle.abnormal('blank');
            if (!visit.abnormal) sampleTimer = setTimeout(sample, 1000);
        };
        sampleTimer = setTimeout(sample, Math.max(0, 3000 - (performance.now() - visit.startedAt)));
        stopWaitingRef.current = () => clearTimeout(sampleTimer);
        return () => {
            clearTimeout(sampleTimer);
            unsubscribe?.();
        };
    }, [consent.analyticsAllowed, consent.resolution, key, lifecycle]);

    useEffect(() => subscribeApiHttpEvent(FINAL_HTTP_ERROR_EVENT, reportRumPageHttpError), []);

    useEffect(() => {
        const hide = () => {
            getCorePageReady()?.cancel();
            lifecycle.observeOutcome('hidden');
            stopProjectObserver.current();
        };
        const visibility = () => {
            if (document.visibilityState === 'hidden') hide();
        };
        const onError = (event: ErrorEvent) => {
            if (isActionableUnhandledError(event.error ?? event.message))
                lifecycle.abnormal('js_error');
        };
        const onRejection = (event: PromiseRejectionEvent) => {
            if (isActionableUnhandledError(event.reason)) lifecycle.abnormal('js_error');
        };
        window.addEventListener('pagehide', hide);
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onRejection);
        return () => {
            window.removeEventListener('pagehide', hide);
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
            if (activeRuntime?.lifecycle === lifecycle) activeRuntime = null;
            lifecycle.dispose();
        };
    }, [lifecycle]);

    return (
        <PageContext.Provider value={value}>
            <Suspense fallback={null}><RumPageNavigation onChange={setLocation} /></Suspense>
            {children}
        </PageContext.Provider>
    );
}

/** 既有 Next 路由边界和局部边界只通知观测结果，不替换错误 UI。 */
export function reportRumPageBoundary(error: Error, boundary: 'route' | 'region', region: string, blocking = true): void {
    getCorePageReady()?.cancel();
    activeRuntime?.lifecycle.boundary(error, boundary, region, blocking);
}
